import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { projectsApi } from '../../api/endpoints.js';
import type { ProjectLocation } from '../../api/endpoints.js';
import { ApiError } from '../../api/client.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,

  ConfirmDialog,
  Dialog,
  ErrorState,
  Form,
  FormSection,
  LoadingState,
  NumberField,
  Panel,
  StatusBadge,
  SwitchField,
  TextAreaField,
  TextField,
  useToast,
} from '../../components/ui/index.js';
import { formatDate } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { optionalText } from '../clients/bilingual.js';
import {
  ContextItem,
  Facts,
  RecordGrid,
  RecordHeader,
  RecordLink,
} from '../clients/record-layout.js';

/**
 * The project file, and the locations on it.
 *
 * A LOCATION IS DEACTIVATED, NEVER DELETED. Material already taken from a
 * location was really taken from there, and a Phase 7 sample will point at
 * this row for years.
 *
 * COORDINATES ARE A PAIR. Both or neither: half a coordinate puts a point on a
 * map that means nothing, and the server refuses it.
 *
 * NOTHING HERE DESCRIBES A TEST. A location is geographic and commercial —
 * where the material came from. Sample detail is Phase 7 and scientific detail
 * is gated behind CLAUDE.md section 4.
 */

interface LocationDraft {
  name: string;
  description: string;
  addressLine: string;
  city: string;
  latitude: number | null;
  longitude: number | null;
  notes: string;
}

function emptyDraft(): LocationDraft {
  return {
    name: '',
    description: '',
    addressLine: '',
    city: '',
    latitude: null,
    longitude: null,
    notes: '',
  };
}

function draftFrom(location: ProjectLocation): LocationDraft {
  return {
    name: location.name,
    description: location.description ?? '',
    addressLine: location.addressLine ?? '',
    city: location.city ?? '',
    latitude: location.latitude === null ? null : Number(location.latitude),
    longitude: location.longitude === null ? null : Number(location.longitude),
    notes: location.notes ?? '',
  };
}

export function ProjectFilePage() {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { show } = useToast();
  const locale = currentLocale();

  const canEdit = can(PERMISSIONS.PROJECTS_UPDATE);

  const [showInactive, setShowInactive] = useState(true);
  const [editing, setEditing] = useState<ProjectLocation | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [draft, setDraft] = useState<LocationDraft>(emptyDraft());
  const [deactivating, setDeactivating] = useState<ProjectLocation | null>(
    null,
  );

  const project = useQuery({
    queryKey: ['project', id],
    queryFn: () => projectsApi.get(id),
    enabled: Boolean(id),
  });

  const locations = useQuery({
    queryKey: ['project-locations', id, showInactive],
    queryFn: () => projectsApi.listLocations(id, showInactive),
    enabled: Boolean(id),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['project-locations', id] });

  const payload = () => ({
    name: draft.name.trim(),
    description: optionalText(draft.description),
    addressLine: optionalText(draft.addressLine),
    city: optionalText(draft.city),
    latitude: draft.latitude,
    longitude: draft.longitude,
    notes: optionalText(draft.notes),
  });

  const save = useMutation({
    mutationFn: () =>
      editing
        ? projectsApi.updateLocation(id, editing.id, payload())
        : projectsApi.createLocation(id, payload()),
    onSuccess: async () => {
      await refresh();
      setEditing(null);
      setIsCreating(false);
      setDraft(emptyDraft());
      show({ tone: 'success', message: t('locations.saved') });
    },
  });

  const setActive = useMutation({
    mutationFn: (input: { location: ProjectLocation; isActive: boolean }) =>
      projectsApi.updateLocation(id, input.location.id, {
        isActive: input.isActive,
      }),
    onSuccess: async () => {
      await refresh();
      setDeactivating(null);
      show({ tone: 'success', message: t('locations.saved') });
    },
  });

  if (project.isLoading) return <LoadingState label={t('common.loading')} />;
  if (project.error) {
    return (
      <ErrorState error={project.error} onRetry={() => void project.refetch()} />
    );
  }
  if (!project.data) return null;

  const record = project.data;

  const closedError =
    save.error instanceof ApiError &&
    save.error.details?.some((detail) => detail.code === 'project_closed');
  const pairError =
    save.error instanceof ApiError &&
    save.error.code === 'VALIDATION_FAILED' &&
    save.error.details?.some((detail) => detail.field === 'longitude');

  const openCreate = () => {
    setDraft(emptyDraft());
    setEditing(null);
    setIsCreating(true);
    save.reset();
  };

  const openEdit = (location: ProjectLocation) => {
    setDraft(draftFrom(location));
    setEditing(location);
    setIsCreating(false);
    save.reset();
  };

  const closeDialog = () => {
    setEditing(null);
    setIsCreating(false);
    save.reset();
  };

  const notRecorded = t('common.notRecorded');
  const clientLabel = record.clientName ?? record.clientNumber;

  return (
    <>
      <RecordHeader
        {...(record.reference ? { number: record.reference } : {})}
        badges={
          <StatusBadge
            label={t(`projects.status.${record.status}`)}
            tone={record.status === 'active' ? 'success' : 'neutral'}
          />
        }
        title={record.name ?? t('projects.fileTitle')}
        context={
          <ContextItem label={t('projects.client')}>
            <RecordLink to={`/clients/${record.clientId}`}>{clientLabel}</RecordLink>
          </ContextItem>
        }
        breadcrumbs={[
          { label: t('nav.projects'), to: '/projects' },
          { label: t('projects.fileTitle') },
        ]}
        actions={
          <ActionBar>
            <ActionButton
              priority="tertiary"
              onPress={() => navigate(`/clients/${record.clientId}`)}
            >
              {t('projects.client')}
            </ActionButton>
            {canEdit ? (
              <ActionButton
                priority="secondary"
                onPress={() => navigate(`/projects/${id}/edit`)}
              >
                {t('common.edit')}
              </ActionButton>
            ) : null}
          </ActionBar>
        }
      />

      {!record.acceptsNewWork ? (
        <div className="mb-stack">
          <Alert
            tone="warning"
            title={t('projects.closedBanner')}
            {...(record.statusReason
              ? { description: record.statusReason }
              : {})}
          />
        </div>
      ) : null}

      <RecordGrid
        side={
          <>
            <Panel title={t('common.details')}>
              <Facts
                items={[
                  {
                    label: t('projects.client'),
                    value: <RecordLink to={`/clients/${record.clientId}`}>{clientLabel}</RecordLink>,
                  },
                  {
                    label: t('projects.reference'),
                    value: record.reference ? (
                      <span dir="ltr" className="measure">
                        {record.reference}
                      </span>
                    ) : (
                      notRecorded
                    ),
                  },
                  {
                    label: t('projects.startDate'),
                    value: record.startDate
                      ? formatDate(record.startDate, { locale })
                      : notRecorded,
                  },
                  {
                    label: t('projects.expectedEndDate'),
                    value: record.expectedEndDate
                      ? formatDate(record.expectedEndDate, { locale })
                      : notRecorded,
                  },
                ]}
              />
            </Panel>

            <Panel
              title={t('projects.parties')}
              description={t('projects.partiesHint')}
            >
              {/* Each says "not recorded" rather than showing a blank, because a
                  blank looks like a rendering fault and this is a fact. */}
              <Facts
                items={[
                  {
                    label: t('projects.contractorNameEn'),
                    value: record.contractorName ? <bdi>{record.contractorName}</bdi> : notRecorded,
                  },
                  {
                    label: t('projects.consultantNameEn'),
                    value: record.consultantName ? <bdi>{record.consultantName}</bdi> : notRecorded,
                  },
                  {
                    label: t('projects.ownerNameEn'),
                    value: record.ownerName ? <bdi>{record.ownerName}</bdi> : notRecorded,
                  },
                ]}
              />
            </Panel>
          </>
        }
      >
        <Panel
          title={t('locations.title')}
          description={t('locations.subtitle')}
          actions={
            <div className="flex flex-wrap items-center gap-inline">
              <SwitchField
                label={t('locations.showInactive')}
                value={showInactive}
                onChange={setShowInactive}
              />
              {canEdit && record.acceptsNewWork ? (
                <ActionButton priority="secondary" size="sm" onPress={openCreate}>
                  {t('locations.add')}
                </ActionButton>
              ) : null}
            </div>
          }
        >
          {locations.isLoading ? <LoadingState /> : null}
          {locations.error ? (
            <ErrorState
              error={locations.error}
              onRetry={() => void locations.refetch()}
            />
          ) : null}

          {!locations.isLoading && (locations.data?.length ?? 0) === 0 ? (
            <p className="text-body text-default-600">
              {t('locations.noneYet')}
            </p>
          ) : null}

          {/* One row per location, divided by hairlines — not a card each. */}
          {(locations.data?.length ?? 0) > 0 ? (
            <ul className="-my-3 flex flex-col divide-y divide-default-200">
              {(locations.data ?? []).map((location) => (
                <li
                  key={location.id}
                  className="flex flex-wrap items-start gap-x-stack gap-y-2 py-3"
                >
                  <div className="min-w-0 flex-1 basis-56">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-body font-semibold text-default-900" dir="auto">
                        {location.name ?? notRecorded}
                      </p>
                      {location.isActive ? null : (
                        <StatusBadge
                          size="sm"
                          tone="neutral"
                          label={t('common.inactive')}
                        />
                      )}
                    </div>
                    <p className="text-caption text-default-600" dir="auto">
                      {location.addressLine ?? notRecorded}
                    </p>
                    {location.latitude && location.longitude ? (
                      <p className="measure text-caption text-default-600" dir="ltr">
                        {location.latitude}, {location.longitude}
                      </p>
                    ) : null}
                  </div>

                  {canEdit ? (
                    <ActionBar>
                      {location.isActive ? (
                        <ActionButton
                          priority="tertiary"
                          size="sm"
                          onPress={() => setDeactivating(location)}
                        >
                          {t('locations.deactivate')}
                        </ActionButton>
                      ) : (
                        <ActionButton
                          priority="tertiary"
                          size="sm"
                          onPress={() =>
                            setActive.mutate({ location, isActive: true })
                          }
                        >
                          {t('locations.reactivate')}
                        </ActionButton>
                      )}
                      <ActionButton
                        priority="secondary"
                        size="sm"
                        onPress={() => openEdit(location)}
                      >
                        {t('common.edit')}
                      </ActionButton>
                    </ActionBar>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </Panel>
      </RecordGrid>

      <Dialog
        isOpen={isCreating || editing !== null}
        onClose={closeDialog}
        title={editing ? t('locations.editTitle') : t('locations.addTitle')}
        size="lg"
      >
        {closedError ? (
          <div className="mb-stack">
            <Alert tone="warning" title={t('locations.projectClosed')} />
          </div>
        ) : null}
        {pairError ? (
          <div className="mb-stack">
            <Alert tone="warning" title={t('locations.coordinatesPaired')} />
          </div>
        ) : null}

        <Form
          onSubmit={() => save.mutate()}
          isSubmitting={save.isPending}
          actions={
            <ActionBar>
              <ActionButton priority="tertiary" onPress={closeDialog}>
                {t('common.cancel')}
              </ActionButton>
              <ActionButton
                priority="primary"
                type="submit"
                isLoading={save.isPending}
              >
                {t('common.save')}
              </ActionButton>
            </ActionBar>
          }
        >
          <FormSection title={t('common.details')}>
            <TextField
              label={t('locations.name')}
              value={draft.name}
              onChange={(value) => setDraft((c) => ({ ...c, name: value }))}
              textDirection="auto"
              isRequired
            />
            <TextField
              label={t('locations.description')}
              value={draft.description}
              onChange={(value) =>
                setDraft((c) => ({ ...c, description: value }))
              }
              textDirection="auto"
            />
            <TextField
              label={t('locations.address')}
              value={draft.addressLine}
              onChange={(value) =>
                setDraft((c) => ({ ...c, addressLine: value }))
              }
              textDirection="auto"
            />
            <TextField
              label={t('locations.city')}
              value={draft.city}
              onChange={(value) => setDraft((c) => ({ ...c, city: value }))}
              textDirection="auto"
            />
          </FormSection>

          <FormSection
            title={t('locations.coordinates')}
            description={t('locations.coordinatesHint')}
            columns={2}
          >
            {/* Null, not zero. An empty coordinate field is an unrecorded
                coordinate, and 0,0 is a real place in the Gulf of Guinea. */}
            <NumberField
              label={t('locations.latitude')}
              value={draft.latitude}
              onChange={(value) => setDraft((c) => ({ ...c, latitude: value }))}
              step="0.000001"
            />
            <NumberField
              label={t('locations.longitude')}
              value={draft.longitude}
              onChange={(value) => setDraft((c) => ({ ...c, longitude: value }))}
              step="0.000001"
            />
          </FormSection>

          <FormSection title={t('locations.notes')}>
            <TextAreaField
              label={t('locations.notes')}
              value={draft.notes}
              onChange={(value) => setDraft((c) => ({ ...c, notes: value }))}
              rows={2}
            />
          </FormSection>
        </Form>
      </Dialog>

      <ConfirmDialog
        isOpen={deactivating !== null}
        onClose={() => setDeactivating(null)}
        title={t('locations.deactivateTitle')}
        message={t('locations.deactivateMessage')}
        confirmLabel={t('locations.deactivate')}
        confirmPriority="destructive"
        isPending={setActive.isPending}
        onConfirm={() => {
          if (deactivating) {
            setActive.mutate({ location: deactivating, isActive: false });
          }
        }}
      />
    </>
  );
}
